// One place for "what does this lock fit". The chooser, the category filters, the product
// page's glance strip and the chooser audit test all read these, so a lock cannot sit in one
// section on the hub and a different one in the chooser. CommonJS, no spread/destructuring
// (CRA content gotcha).
const CHOOSER_STATUSES = ['sale', 'install-only'];

const isDigitalLock = (p) => (p.categories || []).indexOf('digital-door-locks') !== -1;
// 'quote' and 'coming-soon' locks are listed in their lanes but never recommended.
const isOrderable = (p) => CHOOSER_STATUSES.indexOf(p.saleStatus || 'sale') !== -1;
const isSliding = (p) => !!(p.fit && p.fit.sliding);
const hasFingerprint = (p) => (p.unlock || []).some((u) => /fingerprint/i.test(u));
// Apple Home / Google Home / Alexa: the "from my phone" answer.
const hasEcosystem = (p) => (p.ecosystems || []).length > 0;
const hasApple = (p) => (p.ecosystems || []).some((e) => /apple/i.test(e));
const hasWifi = (p) => (p.connectivity || []).some((c) => /built in/i.test(c));
// The lock's own app (Bluetooth or Wi-Fi) — the fallback when no ecosystem lock fits the door.
const hasApp = (p) => (p.unlock || []).concat(p.connectivity || []).some((u) => /\bapp\b|wi-fi/i.test(u));
// A code lock with no electronics (Carbine): nothing to charge, nothing to pair.
const isMechanical = (p) => (p.unlock || []).some((u) => /mechanical/i.test(u));
// `fit.faceMounted` marks a lock that sits on the door face instead of in a pocket (a retrofit
// over the cylinder, a rim lock): the backset is irrelevant, but the body needs a timber or
// wide door — there is no room on a narrow aluminium stile.
const isFaceMounted = (p) => !!(p.fit && p.fit.faceMounted);
// Fits over the existing cylinder, so the existing lock (and its backset) stays (Aqara U200).
const isRetrofit = (p) => !!(p.fit && /retrofit/i.test(p.fit.lockType || ''));
// A straight swap onto a 54 mm round-bore door: deadbolts and bore lever sets.
const isBoreSwap = (p) => !isSliding(p) && !isFaceMounted(p) && (p.categories || []).indexOf('deadbolts-and-levers') !== -1 && !!p.install && p.install.tier === 'swap';
// The two halves of the bore-swap family: `fit.subType` on the product, else read off the
// lock type. A deadbolt replaces a deadbolt (or a deadlatch, with new holes); a lever set
// replaces a keyed knob or lever latch.
const subType = (p) => {
  if (p.fit && p.fit.subType) return p.fit.subType;
  const t = (p.fit && p.fit.lockType) || '';
  return /lever|latch|entrance/i.test(t) && !/deadbolt,|deadbolt$/i.test(t) ? 'lever-set' : 'deadbolt';
};
const isDeadbolt = (p) => isBoreSwap(p) && subType(p) === 'deadbolt';
const isLeverSet = (p) => isBoreSwap(p) && subType(p) === 'lever-set';
// A rim lock mounts on the inside face like the deadlatch it replaces (Schlage S-480): no
// bore, no mortice; fitted at the two-hour deadbolt / latch rate, so the tier does not tell
// it apart from a bore swap — the face mounting does.
const isRim = (p) => !isSliding(p) && !isRetrofit(p) && isFaceMounted(p);
// A lock cut into the door edge (mortice): fit is decided by the backset.
const isMortice = (p) => !isSliding(p) && !isBoreSwap(p) && !isRetrofit(p) && !isRim(p) && !!p.install && p.install.tier === 'cut';
const backsets = (p) => (p.fit && p.fit.backsetsMm) || [];
// An empty backset list on a lock that is not face-mounted means "not confirmed yet", and
// such a lock is never a pick for a given door (Schlage Artus hinged).
const backsetKnown = (p) => backsets(p).length > 0 || isFaceMounted(p);
// 'narrow' = a narrow aluminium stile (30–45 mm); 'standard' = timber or a wide aluminium door (50–70 mm).
const fitsBackset = (p, k) => {
  if (isFaceMounted(p)) return k === 'standard';
  const b = backsets(p);
  return k === 'narrow' ? b.some((x) => x <= 45) : b.some((x) => x >= 50);
};
// Made for a narrow aluminium stile: any backset of 45 mm or less.
const isNarrowStile = (p) => !isSliding(p) && backsets(p).some((x) => x <= 45);
// Only comes in 50–70 mm: the timber mortice and wide-door sizes.
const isStandardOnly = (p) => !isSliding(p) && backsets(p).length > 0 && backsets(p).every((x) => x >= 50);
// The product's own door-material list is a hard filter (Omnia is timber only). An unknown or
// "not sure" material filters nothing.
const fitsMaterial = (p, material) => {
  if (!material || material === 'unsure') return true;
  return ((p.fit && p.fit.doorMaterials) || []).indexOf(material) !== -1;
};
// 'keeps' (a retrofit that leaves the lift-to-lock mechanism in place) or 'provides' (a
// four-point lock of its own): the answers for a multipoint door.
const multipoint = (p) => (p.fit && p.fit.multipoint) || null;

// The one reading of a maker's weather rating, shared by the chooser chips, the product
// page's glance strip and the fine print. "Not IP-rated", "sheltered", "indoor", "apartment"
// and "not published" all mean: keep it under cover. Only a stated rating earns "exposed".
const weatherClass = (p) => {
  const w = (p.fit && p.fit.weatherRating) || '';
  if (/not ip|sheltered|indoor|apartment|not published|not for entries/i.test(w)) return 'sheltered';
  if (/IP\s?[56]\d|IPX[45]|weather[- ]?(resistant|rated)|outdoor|exterior/i.test(w)) return 'exposed';
  return 'unclear';
};
const isShelteredOnly = (p) => weatherClass(p) === 'sheltered';

// Which of the "what is on the door now" answers a lock can replace.
//   mortice / multipoint → a mortice lock (cut to the backset) or the retrofit over the cylinder
//   deadbolt / gripset   → a deadbolt onto the 54 mm bore that is already there
//   nightlatch           → a deadbolt (new holes) or a rim lock on the face like the old one
//   lever                → a lever set onto the bore of the keyed knob or lever
//   none                 → a mortice lock, cut in on the day
//   other                → nothing: photos and a locksmith instead
const fitsOnDoor = (p, onDoor) => {
  if (isSliding(p)) return false;
  if (onDoor === 'deadbolt' || onDoor === 'gripset') return isDeadbolt(p);
  if (onDoor === 'nightlatch') return isDeadbolt(p) || isRim(p);
  if (onDoor === 'lever') return isLeverSet(p);
  if (onDoor === 'mortice' || onDoor === 'multipoint') return isMortice(p) || isRetrofit(p);
  if (onDoor === 'none') return isMortice(p);
  return false;
};

module.exports = {
  isDigitalLock, isOrderable, isSliding, hasFingerprint, hasEcosystem, hasApple, hasWifi, hasApp, isMechanical,
  isBoreSwap, subType, isDeadbolt, isLeverSet, isRim, isRetrofit, isMortice, isFaceMounted,
  backsets, backsetKnown, fitsBackset, isNarrowStile, isStandardOnly, fitsMaterial, multipoint,
  weatherClass, isShelteredOnly, fitsOnDoor,
};
